import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, SunIcon, MoonIcon } from 'lucide-react';
interface NavbarProps {
  isDark: boolean;
  toggleTheme: () => void;
}
export function Navbar({
  isDark,
  toggleTheme
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    // Passive: this listener never calls preventDefault, so declaring that up
    // front lets the browser keep scrolling smooth instead of waiting on us.
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Services',
    href: '/#services'
  }, {
    name: 'About',
    href: '/#about'
  }, {
    name: 'Contact',
    href: '/#contact'
  }];
  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white dark:bg-[#0f1629] md:bg-white/90 md:dark:bg-[#0f1629]/90 md:backdrop-blur-md border-b border-slate-200 dark:border-white/10 py-3 shadow-sm dark:shadow-none' : 'bg-transparent py-5'}`} data-id="element-144" data-mp="epdknphf8ns6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-145" data-mp="3s2gqzl320cxl">
        <div className="flex justify-between items-center" data-id="element-146" data-mp="3s2ysox8hjjcw">
          {/* Logo */}
          <Link to="/" className="flex items-center group -my-1" data-id="element-147" data-mp="vdzstn7u6nd">
            <img src="https://cdn.magicpatterns.com/uploads/miVDHRURGJeqgpYC7Z9kCF/logo.png" alt="Scale Finance Strategies" className={`w-auto object-contain dark:brightness-0 dark:invert transition-all duration-300 ${isScrolled ? 'h-10' : 'h-12'}`} style={{
            width: "305px",
            height: "121px"
          }} data-id="element-148" data-mp="5ne5l2pyvtrv" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8" data-id="element-149" data-mp="319p0ou3pzn5p">
            <Link to="/#services" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-150" data-mp="2wcq3g2orf03a">
              Services
            </Link>

            <Link to="/#about" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-151" data-mp="362rsawoxsmj9">
              About
            </Link>

            <Link to="/calculator" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-152" data-mp="2sb4mo693u1nt">
              Calculator
            </Link>

            <Link to="/#contact" className="text-sm font-medium text-slate-600 hover:text-primary-600 dark:text-slate-400 dark:hover:text-white transition-colors" data-id="element-153" data-mp="7ijbuag4619b">
              Contact
            </Link>

            <div className="flex items-center gap-3 pl-6 border-l border-slate-200 dark:border-white/10" data-id="element-154" data-mp="f4eqtxrya5dy">
              <button onClick={toggleTheme} className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10 transition-colors" aria-label="Toggle theme" data-id="element-155" data-mp="14vus3ceyki0">
                {isDark ? <SunIcon className="w-5 h-5" data-id="element-156" data-mp="xuwg1sdkqiuk" /> : <MoonIcon className="w-5 h-5" data-id="element-157" data-mp="xw7kzvir9wn9" />}
              </button>
              <Link to="/#contact" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-accent-500 to-primary-500 text-white text-sm font-semibold transition-all hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] hover:scale-[1.02]" data-id="element-158" data-mp="k0vrfrgc6c7x">
                Book a Strategy Session
              </Link>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden flex items-center gap-3" data-id="element-159" data-mp="3ozuc9qnoteqo">
            <button onClick={toggleTheme} className="p-2 rounded-full text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10 transition-colors" aria-label="Toggle theme" data-id="element-160" data-mp="3k1st75q9hff7">
              {isDark ? <SunIcon className="w-5 h-5" data-id="element-161" data-mp="bnk78gi8vmw0" /> : <MoonIcon className="w-5 h-5" data-id="element-162" data-mp="bovdqo32r8qt" />}
            </button>
            <button className="p-2 text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-163" data-mp="6oppzizxe6e0">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-164" data-mp="uf3dqxbh9j2s" /> : <MenuIcon className="w-6 h-6" data-id="element-165" data-mp="pmjvvo5rc1s6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 right-0 bg-white/95 dark:bg-[#0f1629]/95 backdrop-blur-xl border-b border-slate-200 dark:border-white/10 py-4 px-4 flex flex-col gap-2 shadow-xl max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-166" data-mp="2d8d5uzk0v0k9">
          <Link to="/#services" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-167" data-mp="2maw6re48z33v">
            Services
          </Link>

          <Link to="/#about" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-168" data-mp="34zko5q3iuqaw">
            About
          </Link>

          <Link to="/calculator" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-169" data-mp="1ewioegs0y2au">
            Calculator
          </Link>

          <Link to="/#contact" className="text-base font-medium text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-white p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-170" data-mp="1xlytlhenmixl">
            Contact
          </Link>

          <Link to="/#contact" className="mt-4 text-center px-5 py-3 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold shadow-md" onClick={() => setIsMobileMenuOpen(false)} data-id="element-171" data-mp="22mmr8lxe1y0s">
            Book a Strategy Session
          </Link>
        </div>}
    </nav>;
}